import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import {
  ClearRounded,
  LaunchRounded,
  LinkRounded,
  UpdateRounded
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
  UpdateProjectDocumentWorkflow,
  UpdateWorkflowStep
} from '../../api/project-document-workflows';
import { RootState } from '../../app/store';
import ApproveChip from '../../components/ApproveChip';
import DocRevisionInfoCard from '../../components/DocRevisionInfoCard';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { SHOW_LINK_DUE_DATE, WORKFLOW_LINK_DUE_DATE } from '../../constants';
import checkWebUrl from '../../functions/checkWebUrl';
import mapExistingUserInWorkflowStep from '../../functions/mapExistingUserInWorkflowStep';
import selectOnlyOneUser from '../../functions/selectOnlyOneUser';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import { getCommentFileAsync } from '../CommentFile/commentFileSlice';
import loadingSlice from '../Loading/loadingSlice';
import { getApprovalCodesAsync } from '../MasterData/ApprovalCode/approvalCodeSlice';
import { getApprovalTypesAsync } from '../MasterData/ApprovalType/approvalTypeSlice';
import { WorkflowStatusLabel } from '../ProjectDocumentRevision/DocumentRevisionView';
import { User } from '../UserProfile/userProfileSlice';
import {
  getProjectDocumentWorkflowAsync,
  stopProjectDocumentWorkflowAsync,
  updateProjectDocumentWorkflowAsync
} from './projectDocumentWorkflowSlice';

interface DocumentWorkflow {
  name: string | null;
  linkName: string | null;
  linkUrl: string | null;
  projectDocumentWorkflowSteps: UpdateWorkflowStep[];
}

export default function DocumentWorkflowEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { users } = useSelector((state: RootState) => state.projectRegisters);
  const { data: approvalCodes } = useSelector(
    (state: RootState) => state.approvalCodes
  );
  const { selectedProjectDocumentWorkflow } = useSelector(
    (state: RootState) => state.projectDocumentWorkflows
  );
  const { selectedProjectDocumentRevision } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const params = useParams<{ docFlowId: string }>();
  const docFlowId = Number.parseInt(params.docFlowId, 10);

  const [documentWorkflow, setDocumentWorkflow] = useState<DocumentWorkflow>({
    name: null,
    linkName: null,
    linkUrl: null,
    projectDocumentWorkflowSteps: []
  });
  const moreThanOneUserInWorkflowStep =
    documentWorkflow.projectDocumentWorkflowSteps.some(
      (projectDocumentWorkflowStep) =>
        selectOnlyOneUser(projectDocumentWorkflowStep.id) &&
        projectDocumentWorkflowStep.users.length > 1
    );
  const [openStop, setOpenStop] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (selectedProjectDocumentWorkflow) {
      setDocumentWorkflow({
        name: selectedProjectDocumentWorkflow.name,
        linkName: selectedProjectDocumentWorkflow.linkName,
        linkUrl: selectedProjectDocumentWorkflow.linkUrl,
        projectDocumentWorkflowSteps:
          selectedProjectDocumentWorkflow.projectDocumentWorkflowSteps.map(
            (documentWorkflowStep) => {
              const userApprovalsNotAnswer =
                documentWorkflowStep.userApprovals.filter(
                  (userApproval) => !Boolean(userApproval.approvalTypeId)
                );
              return {
                id: documentWorkflowStep.workflowStepId,
                documentWorkflowStepId: documentWorkflowStep.id,
                name: documentWorkflowStep.workflowStep.name,
                order: documentWorkflowStep.workflowStep.order,
                isInternal: documentWorkflowStep.workflowStep.isInternal,
                canStopWorkflow:
                  documentWorkflowStep.workflowStep.canStopWorkflow,
                isFinalApprover:
                  documentWorkflowStep.workflowStep.isFinalApprover,
                isChecklist: documentWorkflowStep.workflowStep.isChecklist,
                isRequired: true,
                users: mapExistingUserInWorkflowStep(
                  userApprovalsNotAnswer,
                  users
                ),
                dueDate: new Date(documentWorkflowStep.dueDate)
              };
            }
          )
      });
    }
  }, [selectedProjectDocumentWorkflow, users]);

  useEffect(() => {
    dispatch(getProjectDocumentWorkflowAsync(docFlowId));
    dispatch(getApprovalCodesAsync());
    dispatch(getApprovalTypesAsync());
  }, []);

  async function downloadCommentFile({
    commentFileId,
    contentType,
    filename
  }: {
    commentFileId: number;
    contentType: string;
    filename: string;
  }) {
    await dispatch(
      getCommentFileAsync({
        commentFileId,
        contentType,
        filename
      })
    ).unwrap();
    dispatch(
      loadingSlice.actions.success({
        message: t('projectDocumentWorkflowEdit.messages.fileDownloadedSuccessfully') as string
      })
    );
  }

  function onChangeByField(
    projectWorkflowStepId: number,
    value: User[],
    fieldName: string,
    options?: { selectOneUser: boolean }
  ) {
    let newValue = value;
    const { projectDocumentWorkflowSteps } = documentWorkflow;
    const documentWorkflowStepIndex = projectDocumentWorkflowSteps.findIndex(
      (projectWorkflowStep) => projectWorkflowStep.id === projectWorkflowStepId
    );
    // For select ONLY one user
    if (options?.selectOneUser && value) {
      const newUsers = value as User[];
      const currentUsers =
        projectDocumentWorkflowSteps[documentWorkflowStepIndex].users;
      const isReplaceUser = currentUsers.length < newUsers.length;
      if (isReplaceUser) {
        newValue = newUsers.filter((newUser) =>
          currentUsers.every((currentUser) => currentUser.id !== newUser.id)
        );
      } else {
        newValue = newUsers;
      }
    }
    const newProjectWorkflowStep = {
      ...projectDocumentWorkflowSteps[documentWorkflowStepIndex],
      [fieldName]: newValue
    };
    const newProjectWorkflowSteps = [
      ...projectDocumentWorkflowSteps.slice(0, documentWorkflowStepIndex),
      newProjectWorkflowStep,
      ...projectDocumentWorkflowSteps.slice(documentWorkflowStepIndex + 1)
    ];
    setDocumentWorkflow({
      ...documentWorkflow,
      projectDocumentWorkflowSteps: newProjectWorkflowSteps
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (selectedProjectDocumentWorkflow && documentWorkflow.name) {
        const updateProjectDocumentWorkflow: UpdateProjectDocumentWorkflow = {
          id: selectedProjectDocumentWorkflow.id,
          projectDocumentRevisionId:
            selectedProjectDocumentWorkflow.projectDocumentRevisionId,
          name: documentWorkflow.name,
          linkName: documentWorkflow.linkName,
          linkUrl: documentWorkflow.linkUrl,
          isAutoApproval: Boolean(
            selectedProjectDocumentWorkflow.isAutoApproval
          ),
          isCreatorViewOnlyExternal: Boolean(
            selectedProjectDocumentWorkflow.isCreatorViewOnlyExternal
          ),
          isCreatorViewOnlyFinal: Boolean(
            selectedProjectDocumentWorkflow.isCreatorViewOnlyFinal
          ),
          projectDocumentWorkflowSteps:
            documentWorkflow.projectDocumentWorkflowSteps
        };
        await dispatch(
          updateProjectDocumentWorkflowAsync(updateProjectDocumentWorkflow)
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('projectDocumentWorkflowEdit.messages.workflowUpdated') as string
          })
        );
        setTimeout(() => {
          history.push(
            `/document-revisions/${selectedProjectDocumentWorkflow.projectDocumentRevisionId}`
          );
        }, 1000);
      }
    } catch (error) {
      setLoading(false);
    }
  }

  function onStopWorkflow() {
    if (selectedProjectDocumentWorkflow) {
      setLoading(true);
      dispatch(
        stopProjectDocumentWorkflowAsync(selectedProjectDocumentWorkflow.id)
      );
      setTimeout(() => {
        history.push(
          `/document-revisions/${selectedProjectDocumentWorkflow.projectDocumentRevisionId}`
        );
      }, 1000);
    }
  }

  return (
    <div className={classes.root}>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={openStop}
        onClose={() => setOpenStop(false)}
      >
        <DialogTitle id="title">{t('projectDocumentWorkflowEdit.dialogs.areYouSure')}</DialogTitle>
        <div className={classes.flexAction}>
          <Button
            className={classes.redColor}
            fullWidth
            color="primary"
            variant="contained"
            onClick={onStopWorkflow}
            disabled={loading}
          >
            {t('projectDocumentWorkflowEdit.buttons.stopWorkflow')}
          </Button>
          <Button
            fullWidth
            onClick={() => setOpenStop(false)}
            disabled={loading}
          >
            {t('projectDocumentWorkflowEdit.buttons.cancel')}
          </Button>
        </div>
      </Dialog>
      <Breadcrumb
        name="editDocumentWorkflow"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id,
          revisionName: `${t('projectDocumentWorkflowEdit.labels.purpose')}: ${selectedProjectDocumentRevision?.purpose.abbreviation}, ${t('projectDocumentWorkflowEdit.labels.revision')}: ${selectedProjectDocumentRevision?.revision.name}`,
          revisionId: selectedProjectDocumentRevision?.id,
          flowName: selectedProjectDocumentWorkflow?.name
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectDocumentWorkflowEdit.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && selectedProjectDocumentRevision && (
            <DocRevisionInfoCard
              document={selectedProjectDocument}
              docRevision={selectedProjectDocumentRevision}
            />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          {selectedProjectDocumentWorkflow &&
            selectedProjectDocumentWorkflow.workflowStatusId !==
              WorkflowStatusLabel.InProgress && (
              <Alert className={classes.marginBottom1} severity="warning">
                {t('projectDocumentWorkflowEdit.alerts.cannotEdit')} {selectedProjectDocumentWorkflow.workflowStatus.name.toLowerCase()}
              </Alert>
            )}
          <form onSubmit={onSubmit}>
            <Paper className={classes.card}>
              <div className={classes.flexAlignCenter}>
                <Typography
                  className={classes.flexGrow}
                  variant="subtitle1"
                  gutterBottom
                >
                  {t('projectDocumentWorkflowEdit.sections.workflowDetails')}
                </Typography>
                {selectedProjectDocumentWorkflow?.workflowStatusId ===
                  WorkflowStatusLabel.InProgress && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setOpenStop(true)}
                  >
                    {t('projectDocumentWorkflowEdit.buttons.stopWorkflow')}
                  </Button>
                )}
              </div>
              <Divider className={classes.divider} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    className={classes.fullWidth}
                    id="document-flow-name"
                    label={t('projectDocumentWorkflowEdit.labels.workflowName')}
                    variant="outlined"
                    size="small"
                    value={documentWorkflow.name ? documentWorkflow.name : ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setDocumentWorkflow({
                        ...documentWorkflow,
                        name: event.target.value
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                {SHOW_LINK_DUE_DATE && (
                  <>
                    <Grid item xs={12}>
                      <Alert
                        severity="warning"
                        icon={<LinkRounded className={classes.rotate45} />}
                      >
                        {t('projectDocumentWorkflowEdit.alerts.urlFieldRemoval.text1')}{' '}
                        <b>{moment(WORKFLOW_LINK_DUE_DATE).format('ll')}</b>,
                        {t('projectDocumentWorkflowEdit.alerts.urlFieldRemoval.text2')}
                      </Alert>
                    </Grid>
                    <Grid item xs={12} sm={documentWorkflow.linkUrl ? 8 : 12}>
                      <TextField
                        className={classes.fullWidth}
                        id="link-url"
                        label={t('projectDocumentWorkflowEdit.labels.websiteUrl')}
                        variant="outlined"
                        size="small"
                        type="url"
                        value={
                          documentWorkflow.linkUrl
                            ? documentWorkflow.linkUrl
                            : ''
                        }
                        onChange={(event) => {
                          const linkUrl = event.target.value;
                          setDocumentWorkflow({
                            ...documentWorkflow,
                            linkUrl: linkUrl === '' ? null : linkUrl,
                            linkName:
                              linkUrl === '' ? null : documentWorkflow.linkName
                          });
                        }}
                        required={Boolean(documentWorkflow.linkName)}
                      />
                    </Grid>
                    {documentWorkflow.linkUrl && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          className={classes.fullWidth}
                          id="link-name"
                          label={t('projectDocumentWorkflowEdit.labels.linkName')}
                          variant="outlined"
                          size="small"
                          value={
                            documentWorkflow.linkName
                              ? documentWorkflow.linkName
                              : ''
                          }
                          onChange={(event) => {
                            setDocumentWorkflow({
                              ...documentWorkflow,
                              linkName:
                                event.target.value === ''
                                  ? null
                                  : event.target.value
                            });
                          }}
                        />
                      </Grid>
                    )}
                    {documentWorkflow.linkUrl && (
                      <Grid className={classes.linkBox} item xs={12}>
                        <Typography
                          className={clsx(classes.link, classes.marginLR)}
                          component="a"
                          href={checkWebUrl(documentWorkflow.linkUrl)}
                          target="_blank"
                          variant="body1"
                          display="inline"
                        >
                          {documentWorkflow.linkName
                            ? documentWorkflow.linkName
                            : documentWorkflow.linkUrl}
                          <LaunchRounded color="action" fontSize="small" />
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Paper>
            <Paper className={classes.card}>
              <Typography
                className={classes.flexGrow}
                variant="subtitle1"
                gutterBottom
              >
                {t('projectDocumentWorkflowEdit.sections.documentWorkflow')}
              </Typography>
              <Divider className={classes.divider} />
              <Alert severity="info">
                {t('projectDocumentWorkflowEdit.alerts.workflowSteps.text1')}
                <span className={classes.colorRed}> ※</span>, {t('projectDocumentWorkflowEdit.alerts.workflowSteps.text2')}
              </Alert>
              {documentWorkflow.projectDocumentWorkflowSteps.map(
                (documentWorkflowStep, index) => {
                  const isEditUser =
                    documentWorkflowStep.documentWorkflowStepId &&
                    selectedProjectDocumentWorkflow &&
                    documentWorkflowStep.documentWorkflowStepId >=
                      selectedProjectDocumentWorkflow.currentWorkflowStepId;
                  const reviewedUserIds =
                    selectedProjectDocumentWorkflow?.projectDocumentWorkflowSteps
                      .find(
                        (projectDocumentWorkflowStep) =>
                          projectDocumentWorkflowStep.workflowStepId ===
                          documentWorkflowStep.id
                      )
                      ?.userApprovals.filter(
                        (userApproval) => userApproval.approvalTypeId
                      );
                  const reviewedUsers = reviewedUserIds?.map(
                    (reviewedUserId) => {
                      const user = users.find(
                        (user) => user.id === reviewedUserId.userId
                      );
                      return { ...reviewedUserId, user };
                    }
                  );
                  const selectOneUser = selectOnlyOneUser(
                    documentWorkflowStep.id
                  );
                  const showMoreContent =
                    (!selectedProjectDocumentWorkflow?.isCreatorViewOnlyExternal ||
                      !documentWorkflowStep.isInternal) &&
                    (!selectedProjectDocumentWorkflow?.isCreatorViewOnlyFinal ||
                      documentWorkflowStep.isFinalApprover);
                  return (
                    <div key={documentWorkflowStep.id}>
                      <div className={classes.flexFlowStep}>
                        <Avatar className={classes.smallAvatar}>
                          {index + 1}
                        </Avatar>
                        <Typography
                          className={classes.flexGrowName}
                          variant="subtitle1"
                          color="textPrimary"
                        >
                          {documentWorkflowStep.name}
                          {!documentWorkflowStep.canStopWorkflow && (
                            <span className={classes.colorRed}> ※</span>
                          )}
                        </Typography>
                      </div>
                      {reviewedUsers?.map((reviewedUser) => {
                        return (
                          <div
                            className={clsx(
                              classes.userField,
                              classes.marginBottom2
                            )}
                            key={reviewedUser.id}
                          >
                            <Typography
                              className={classes.lineHeight120}
                              variant="h6"
                            >
                              {reviewedUser.user?.firstName}{' '}
                              {reviewedUser.user?.lastName}
                            </Typography>
                            {showMoreContent && reviewedUser.comment && (
                              <Typography
                                variant="body2"
                                style={{
                                  overflowWrap: 'break-word',
                                  whiteSpace: 'pre-wrap'
                                }}
                              >
                                {t('projectDocumentWorkflowEdit.labels.comment')} : {reviewedUser.comment}
                              </Typography>
                            )}
                            {showMoreContent &&
                              reviewedUser.commentFiles.length > 0 && (
                                <div className={classes.commentFileList}>
                                  <Typography
                                    className={classes.marginRight}
                                    variant="body2"
                                  >
                                    {t('projectDocumentWorkflowEdit.labels.commentFiles')} :
                                  </Typography>
                                  {reviewedUser.commentFiles.map(
                                    (file, index) => {
                                      return (
                                        <div
                                          className={classes.marginRight}
                                          key={file.id}
                                        >
                                          <Typography
                                            className={clsx(
                                              classes.filename,
                                              classes.link
                                            )}
                                            onClick={() =>
                                              downloadCommentFile({
                                                commentFileId: file.id,
                                                contentType: file.s3ContentType,
                                                filename: file.name
                                              })
                                            }
                                            variant="caption"
                                          >
                                            {file.name}
                                          </Typography>
                                          {index + 1 <
                                            reviewedUser.commentFiles
                                              .length && (
                                            <Typography variant="caption">
                                              ,
                                            </Typography>
                                          )}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}
                            {reviewedUser.approvalCodeId && (
                              <Typography display="block" variant="caption">
                                {
                                  approvalCodes.find(
                                    (approvalCode) =>
                                      approvalCode.id ===
                                      reviewedUser.approvalCodeId
                                  )?.name
                                }
                              </Typography>
                            )}
                            {reviewedUser.autoApprovalNote && (
                              <Typography display="block" variant="caption">
                                {t('projectDocumentWorkflowEdit.labels.note')} : {reviewedUser.autoApprovalNote}
                              </Typography>
                            )}
                            <ApproveChip
                              approvalTypeId={reviewedUser.approvalTypeId}
                              submittedDate={reviewedUser.submittedDate}
                              isAutoApproval={Boolean(
                                reviewedUser.isAutoApproval
                              )}
                            />
                          </div>
                        );
                      })}
                      {isEditUser && (
                        <>
                          <Autocomplete
                            className={classes.userField}
                            id={`userId-${documentWorkflowStep.id}`}
                            size="small"
                            multiple
                            options={users}
                            getOptionLabel={(option) =>
                              `${option?.firstName} ${option?.lastName}`
                            }
                            renderTags={(tagValue, getTagProps) =>
                              tagValue.map((option, index) => (
                                <Chip
                                  label={`${option?.firstName} ${option?.lastName}`}
                                  {...getTagProps({ index })}
                                  variant="outlined"
                                  size="small"
                                  deleteIcon={<ClearRounded />}
                                />
                              ))
                            }
                            value={documentWorkflowStep.users}
                            getOptionSelected={(option, value) =>
                              option.id === value?.id
                            }
                            onChange={(event, newValue) =>
                              onChangeByField(
                                documentWorkflowStep.id,
                                newValue,
                                'users',
                                { selectOneUser }
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder={
                                  documentWorkflowStep.users.length === 0
                                    ? (t('projectDocumentWorkflowEdit.placeholders.selectUser') as string)
                                    : ''
                                }
                                required={
                                  reviewedUsers?.length === 0 &&
                                  documentWorkflowStep.users.length === 0
                                }
                                error={
                                  selectOneUser &&
                                  documentWorkflowStep.users.length > 1
                                }
                                helperText={
                                  selectOneUser && (t('projectDocumentWorkflowEdit.messages.selectOnlyOnePerson') as string)
                                }
                              />
                            )}
                          />
                          <div className={classes.dueDateField}>
                            <Typography
                              className={classes.flexGrowName}
                              variant="body2"
                              align="right"
                            >
                              {t('projectDocumentWorkflowEdit.labels.dueDate')} :{' '}
                              {moment(documentWorkflowStep.dueDate).format(
                                'LL'
                              )}
                            </Typography>
                          </div>
                        </>
                      )}
                    </div>
                  );
                }
              )}
            </Paper>
            {selectedProjectDocumentWorkflow?.workflowStatusId ===
              WorkflowStatusLabel.InProgress && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={moreThanOneUserInWorkflowStep || loading}
                startIcon={<UpdateRounded />}
              >
                {t('projectDocumentWorkflowEdit.buttons.updateWorkflow')}
              </Button>
            )}
          </form>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fullWidth: {
      width: '100%'
    },
    smallAvatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      backgroundColor: theme.palette.primary.main
    },
    avatarDisabled: {
      backgroundColor: theme.palette.text.disabled
    },
    flexFlowStep: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(2, 0, 1, 0)
    },
    flexGrowName: {
      flexGrow: 1,
      padding: theme.spacing(0, 2),
      lineHeight: '120%'
    },
    userField: {
      width: `calc(100% - ${theme.spacing(8)}px)`,
      marginLeft: theme.spacing(6)
    },
    dueDateField: {
      width: `calc(100% - ${theme.spacing(8)}px)`,
      marginLeft: theme.spacing(6),
      marginTop: theme.spacing(1.5)
    },
    marginBottom1: {
      marginBottom: theme.spacing(1)
    },
    marginBottom2: {
      marginBottom: theme.spacing(2)
    },
    marginRight: {
      marginRight: theme.spacing(1)
    },
    marginLR: {
      margin: theme.spacing(0, 1)
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    flexGrow: {
      flexGrow: 1
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexAction: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1)
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column-reverse',
        width: `calc(100% - ${theme.spacing(2)}px)`,
        '& > *': {
          marginTop: 0
        }
      }
    },
    redColor: {
      backgroundColor: theme.palette.error.main,
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: theme.palette.error.dark
      }
    },
    filename: {
      wordBreak: 'break-all'
    },
    rotate45: {
      rotate: '-45deg'
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.dark
      },
      wordBreak: 'break-all'
    },
    linkBox: {
      borderRadius: theme.spacing(1),
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      margin: theme.spacing(0.8, 1)
    },
    commentFileList: {
      display: 'flex',
      flexWrap: 'wrap',
      wordBreak: 'break-word'
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    colorRed: {
      color: theme.palette.error.main
    }
  })
);
